<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.notificationList.modal.new.buttonText')"
      @click="openNotificationModal = true"
    />
    <export
      url="/notification_plans/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.notifications')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <notification-modal
    v-model:open="openNotificationModal"
    v-model:notification-plan="editedNotificationPlan"
    @saved="refetch"
  />
  <test-notification-modal
    v-model:open="openTestModal"
    :notification-plan="editedNotificationPlan"
  />
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :row-click="rowClickFunction"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
  />
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { enumOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import NotificationModal from '@pages/notifications/NotificationModal.vue';
import { format } from 'date-fns';
import Export from '@utils/toolbar/panes/Export.vue';
import TestNotificationModal from '@pages/notifications/TestNotificationModal.vue';
import type { Company, HydraCollection, NotificationPlan } from '@/entities/Entities';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const openNotificationModal = ref(false);
const editedNotificationPlan = ref();
const openTestModal = ref(false);

const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const [deleteNotificationPlan, { loading: deleting }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const rowClickFunction = function (item: NotificationPlan): void {
  editedNotificationPlan.value = item;
  openNotificationModal.value = true;
};

const actions = [
  {
    title: t('pages.notificationList.actions.edit'),
    action: (item: Company) => {
      openNotificationModal.value = true;
      editedNotificationPlan.value = { ...item };
    },
  },
  {
    title: t('pages.notificationList.actions.test'),
    action: async (item: NotificationPlan) => {
      openTestModal.value = true;
      editedNotificationPlan.value = item;
    },
  },
  {
    title: t('pages.notificationList.actions.delete'),
    action: async (item: Company) => {
      if (await deleteNotificationPlan(`/notification_plans/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.notificationList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'name',
    title: t('notificationPlan.name'),
    sortable: true,
    visible: true,
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('notificationPlan.name'),
      },
    },
  },
  {
    key: 'active',
    csvTranslations: {
      true: t('notificationPlan.active.true'),
      false: t('notificationPlan.active.false'),
    },
    title: t('notificationPlan.active.DEFAULT'),
    sortable: true,
    visible: true,
    formatter: (item: boolean) => t(`notificationPlan.active.${item}`),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'bool',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('notificationPlan.active.DEFAULT'),
        labelTrue: t('notificationPlan.active.true'),
        labelFalse: t('notificationPlan.active.false'),
      },
    },
  },
  {
    key: 'campaignConnection',
    csvTranslations: {
      no: t('notificationPlan.campaignConnection.no'),
      fix: t('notificationPlan.campaignConnection.fix'),
      filter: t('notificationPlan.campaignConnection.filter'),
    },
    title: t('notificationPlan.campaignConnection.DEFAULT'),
    sortable: true,
    visible: true,
    formatter: (item: string) => t(`notificationPlan.campaignConnection.${item}`),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'dropdown',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/NotificationCampaignConnection', {}, (value) => ({
          value,
          label: t(`notificationPlan.campaignConnection.${value}`),
        })),
        label: t('notificationPlan.campaignConnection.DEFAULT'),
      },
    },
  },
  {
    key: 'dateType',
    csvTranslations: {
      fix: t('notificationPlan.dateType.fix'),
      recurrent: t('notificationPlan.dateType.recurrent'),
      event: t('notificationPlan.dateType.event'),
    },
    title: t('notificationPlan.dateType.DEFAULT'),
    sortable: true,
    visible: true,
    formatter: (item: string) => t(`notificationPlan.dateType.${item}`),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'dropdown',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/NotificationDateType', {}, (value) => ({
          value,
          label: t(`notificationPlan.dateType.${value}`),
        })),
        label: t('notificationPlan.dateType.DEFAULT'),
      },
    },
  },
  {
    key: 'lastExecution',
    title: t('notificationPlan.lastExecution'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('notificationPlan.lastExecution'),
      },
    },
  },
  {
    key: 'nextExecution',
    title: t('notificationPlan.nextExecution'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('notificationPlan.nextExecution'),
      },
    },
  },
  {
    key: 'transport',
    title: t('notificationPlan.transports.DEFAULT'),
    sortable: false,
    visible: true,
    csvTranslations: {
      push: t('notificationPlan.transports.push'),
      sms: t('notificationPlan.transports.sms'),
      email: t('notificationPlan.transports.email'),
      zendesk: t('notificationPlan.transports.zendesk'),
      historyEntry: t('notificationPlan.transports.historyEntry'),
    },
    computed: (item: NotificationPlan) => [
      item.push ? t('notificationPlan.transports.push') : undefined,
      item.sms ? t('notificationPlan.transports.sms') : undefined,
      item.email ? t('notificationPlan.transports.email') : undefined,
      item.zenDeskMessage ? t('notificationPlan.transports.zendesk') : undefined,
      item.historyEntryText ? t('notificationPlan.transports.historyEntry') : undefined,
    ].filter((v) => v).join(', '),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'dropdown',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        options: [
          {
            value: 'push',
            label: t('notificationPlan.transports.push'),
          },
          {
            value: 'sms',
            label: t('notificationPlan.transports.sms'),
          },
          {
            value: 'email',
            label: t('notificationPlan.transports.email'),
          },
          {
            value: 'zenDeskMessage',
            label: t('notificationPlan.transports.zendesk'),
          },
          {
            value: 'historyEntryText',
            label: t('notificationPlan.transports.historyEntry'),
          },
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          delete m[key];
          const transports = ['push', 'sms', 'email', 'zenDeskMessage', 'historyEntryText'];
          for (const method of transports) {
            delete m[`exists[${method}]`];
          }
          if (value) {
            m[`exists[${value}]`] = true;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          const transports = ['push', 'sms', 'email', 'zenDeskMessage', 'historyEntryText'];
          for (const method of transports) {
            if (`exists[${method}]` in m) {
              return method;
            }
          }
          return undefined;
        },
        label: t('notificationPlan.transports.DEFAULT'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.notificationList.fieldGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.notificationList.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<NotificationPlan>>('/notification_plans', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}

.medium {
  width: 300px;
  height: auto;
}
</style>

